// Conteúdo das páginas de serviço do alphaclimatizacao.pt
// Fonte única: alimenta o Header, o Footer, a Home, o QuoteForm e a página /servico/:id

export interface ClimatizacaoFaq {
  question: string;
  answer: string;
}

export interface ClimatizacaoStep {
  icon: string;
  title: string;
  description: string;
}

/**
 * Bloco de categoria/gama apresentado com imagem (ou painel com ícone) de um
 * lado e texto do outro. Quando `image` não está preenchida, a página desenha
 * um painel com o ícone — basta acrescentar a foto depois para a substituir.
 */
export interface ClimatizacaoCategoria {
  title: string;
  icon: string;
  description: string;
  image?: string;
  itens: string[];
}

export interface ClimatizacaoService {
  id: string;
  slug: string;
  navTitle: string;
  /** Nome do serviço sem cidade, para compor os títulos das páginas locais. */
  tituloLocal: string;
  formValue: string;
  title: string;
  subtitle: string;
  description: string;
  image: string;
  imagePosition?: string;
  beneficios: string[];
  features: string[];
  fullContent: string;
  categoriasTitle?: string;
  categoriasSubtitle?: string;
  categorias?: ClimatizacaoCategoria[];
  steps?: ClimatizacaoStep[];
  faqs?: ClimatizacaoFaq[];
}

export const climatizacaoServicesList: ClimatizacaoService[] = [
  {
    id: 'manutencao-ar-condicionado',
    slug: 'manutencao-ar-condicionado',
    navTitle: 'Manutenção',
    tituloLocal: 'Manutenção de Ar Condicionado',
    formValue: 'Manutenção de Ar Condicionado',
    title: 'Manutenção de Ar Condicionado em Lisboa',
    subtitle: 'Limpeza, higienização e revisão técnica para equipamentos que duram mais',
    description: 'Manutenção preventiva e corretiva de ar condicionado em Lisboa. Limpeza de filtros e evaporadores, higienização, verificação de carga de gás e diagnóstico de avarias, com contratos anuais para habitações, condomínios e empresas.',
    image: '/images/manutencao-ac.webp',
    imagePosition: 'center 30%',
    beneficios: [
      'Menos consumo elétrico e faturas mais baixas',
      'Ar mais limpo, sem odores nem fungos nas condutas',
      'Deteção de avarias antes de se tornarem caras',
      'Maior vida útil do equipamento',
      'Contrato anual com intervenções programadas'
    ],
    features: [
      'Limpeza e higienização de filtros',
      'Lavagem da unidade evaporadora',
      'Verificação da carga de gás',
      'Teste de componentes elétricos',
      'Limpeza da unidade exterior',
      'Relatório técnico da intervenção'
    ],
    fullContent: `A ALPHA CLIMATIZAÇÃO realiza manutenção de ar condicionado em Lisboa para habitações, condomínios, escritórios, lojas e restaurantes. A manutenção é o serviço mais subestimado da climatização e, ao mesmo tempo, aquele que mais dinheiro poupa: um equipamento sujo consome mais eletricidade, arrefece pior e avaria mais cedo.

Há sinais claros de que o seu ar condicionado precisa de manutenção. O equipamento demora muito mais tempo a arrefecer ou a aquecer a divisão. A fatura de eletricidade subiu sem que tenha mudado hábitos. Sente um cheiro a humidade ou a mofo quando liga o aparelho. Há água a pingar da unidade interior. O aparelho faz ruídos que antes não fazia. Qualquer um destes sintomas justifica uma intervenção técnica.

A nossa manutenção preventiva inclui limpeza e higienização dos filtros, lavagem da unidade evaporadora, desobstrução e desinfeção do tubo de drenagem, limpeza do condensador na unidade exterior, verificação da carga de gás refrigerante, medição de pressões e teste de todos os componentes elétricos. No final entregamos um relatório com o estado do equipamento e com o que deve ser vigiado até à próxima intervenção.

Para uso doméstico normal recomendamos uma manutenção anual, feita idealmente na primavera, antes da época de maior utilização. Em espaços comerciais, restaurantes, ginásios e escritórios com utilização intensiva, o intervalo correto é semestral. Equipamentos com determinadas cargas de gás fluorado estão ainda sujeitos a verificações periódicas de fugas por técnico com certificação própria — tratamos desse acompanhamento pelo cliente.

Trabalhamos com contrato anual de manutenção ou com intervenções avulsas, conforme preferir. O contrato anual garante intervenções programadas, prioridade no atendimento em caso de avaria e condições preferenciais em peças e mão de obra. Peça-nos uma proposta e comparamos consigo as duas opções sem compromisso.`,
    categoriasTitle: 'Tipos de manutenção que fazemos',
    categoriasSubtitle: 'A periodicidade e o plano certo dependem do tipo de espaço e da utilização do equipamento',
    categorias: [
      {
        title: 'Manutenção Doméstica',
        icon: 'ri-home-4-line',
        image: '/images/manutencao-ac.webp',
        description: 'Para apartamentos e moradias com utilização normal. Uma intervenção anual, feita de preferência na primavera, chega para manter o equipamento eficiente e o ar limpo durante toda a época de calor.',
        itens: [
          'Recomendada uma vez por ano',
          'Ideal antes do verão',
          'Split e multi-split',
          'Intervenção em cerca de uma hora'
        ]
      },
      {
        title: 'Restauração e Comércio',
        icon: 'ri-store-2-line',
        image: '/images/manutencao-restauracao-comercio.webp',
        description: 'Restaurantes, cafés, lojas e ginásios têm equipamentos a trabalhar muitas horas por dia, muitas vezes em ambiente com gordura ou pó em suspensão. Aqui o intervalo correto é semestral, e a limpeza tem de ser mais profunda.',
        itens: [
          'Periodicidade semestral',
          'Limpeza profunda de evaporadores',
          'Intervenções fora do horário de serviço',
          'Resposta prioritária em avaria'
        ]
      },
      {
        title: 'Condomínios e Edifícios',
        icon: 'ri-building-4-line',
        image: '/images/manutencao-condominios-edificios.webp',
        description: 'Apoio técnico a administrações de condomínio e a gestores de edifícios, com planos que cobrem várias unidades e um único interlocutor. Entregamos relatório por equipamento, o que facilita a prestação de contas em assembleia.',
        itens: [
          'Planos para múltiplos equipamentos',
          'Relatório técnico por unidade',
          'Orçamento único e faturação centralizada',
          'Agendamento coordenado com moradores'
        ]
      },
      {
        title: 'Contrato Anual de Manutenção',
        icon: 'ri-calendar-check-line',
        image: '/images/contrato-manutencao-ac.webp',
        description: 'A alternativa às intervenções avulsas. Fixa as visitas no calendário, dá prioridade no atendimento quando há avaria e aplica condições preferenciais em peças e mão de obra. Comparamos consigo as duas opções antes de decidir.',
        itens: [
          'Visitas programadas sem ter de se lembrar',
          'Prioridade no atendimento urgente',
          'Desconto em peças e mão de obra',
          'Acompanhamento de gases fluorados'
        ]
      }
    ],
    steps: [
      { icon: 'ri-phone-line', title: 'Contacto e agendamento', description: 'Diz-nos quantos equipamentos tem e onde estão. Agendamos a visita no horário que lhe der jeito.' },
      { icon: 'ri-search-eye-line', title: 'Diagnóstico', description: 'O técnico avalia o estado do equipamento, mede pressões e identifica desgastes ou fugas.' },
      { icon: 'ri-brush-line', title: 'Limpeza e higienização', description: 'Filtros, evaporador, drenagem e unidade exterior. Trabalho protegido, sem sujar o espaço.' },
      { icon: 'ri-file-list-3-line', title: 'Relatório e próximos passos', description: 'Recebe o relatório da intervenção e a recomendação para a próxima manutenção.' }
    ],
    faqs: [
      { question: 'De quanto em quanto tempo devo fazer a manutenção?', answer: 'Uma vez por ano em casa, de seis em seis meses em espaços comerciais ou de utilização intensiva. Se houver animais, obras por perto ou muito pó, o intervalo deve encurtar.' },
      { question: 'A manutenção inclui carga de gás?', answer: 'A verificação da carga está sempre incluída. A recarga é cobrada à parte, porque só é necessária quando existe perda — e, se existir perda, o correto é localizar e reparar a fuga primeiro.' },
      { question: 'Quanto tempo demora?', answer: 'Um split doméstico demora tipicamente entre 45 e 90 minutos. Multi-splits e sistemas comerciais dependem do número de unidades.' },
      { question: 'Fazem manutenção de equipamentos que não instalaram?', answer: 'Sim. Fazemos manutenção de qualquer marca e de equipamentos instalados por terceiros.' }
    ]
  },
  {
    id: 'instalacao-ar-condicionado',
    slug: 'instalacao-ar-condicionado',
    navTitle: 'Instalação',
    tituloLocal: 'Instalação de Ar Condicionado',
    formValue: 'Instalação de Ar Condicionado',
    title: 'Instalação de Ar Condicionado em Lisboa',
    subtitle: 'Do dimensionamento à colocação em serviço, com garantia de instalação',
    description: 'Instalação profissional de ar condicionado em Lisboa: split, multi-split, condutas e VRF. Visita técnica, dimensionamento correto em BTU, execução limpa e teste completo de funcionamento.',
    image: '/images/ar-condicionado-moderno.webp',
    imagePosition: 'center 40%',
    beneficios: [
      'Visita técnica e dimensionamento antes de comprar',
      'Equipamento certo para o espaço — nem a menos, nem a mais',
      'Instalação limpa, com proteção do espaço',
      'Teste completo e explicação do funcionamento',
      'Garantia de instalação e garantia do fabricante asseguradas'
    ],
    features: [
      'Sistemas residenciais e comerciais',
      'Split, multi-split, condutas e VRF',
      'Instalação em altura com meios próprios',
      'Passagem de tubagem e drenagem',
      'Ligação elétrica dedicada',
      'Colocação em serviço e teste de estanquidade'
    ],
    fullContent: `A ALPHA CLIMATIZAÇÃO faz instalação de ar condicionado em Lisboa em habitações, escritórios, lojas, restaurantes e espaços industriais. Instalamos sistemas split, multi-split, ar condicionado de condutas e sistemas VRF, de todas as marcas do mercado.

A instalação começa sempre antes do equipamento. Fazemos uma visita técnica ao local para avaliar a área a climatizar, a orientação solar, o isolamento, o número de ocupantes, os equipamentos que geram calor e as possibilidades de passagem de tubagem. Só com esses dados se define a potência correta em BTU e o tipo de sistema adequado. Um equipamento subdimensionado nunca chega à temperatura pretendida e trabalha sempre no limite; um sobredimensionado liga e desliga constantemente, gasta mais e desumidifica pior.

A execução inclui a fixação das unidades interior e exterior, a passagem e isolamento das linhas de refrigeração, a drenagem de condensados com inclinação correta, a ligação elétrica dedicada, o vácuo do circuito e o teste de estanquidade. Protegemos o espaço antes de começar e deixamos a obra limpa. Sempre que a colocação da unidade exterior exige trabalho em altura, temos meios próprios e seguro para o fazer em segurança.

No fim, colocamos o sistema em serviço, medimos temperaturas e pressões e explicamos-lhe o funcionamento do comando, os modos de operação e os cuidados básicos de utilização. Entregamos a documentação do equipamento e a garantia da instalação, que acresce à garantia do fabricante. Uma instalação mal executada anula, em muitos casos, a garantia do próprio equipamento — é por isso que insistimos no rigor de cada etapa.

Se está a comparar orçamentos, exija sempre que fique escrito o modelo exato, a potência, o comprimento de tubagem incluído e o que acontece se for preciso mais. É aí que a maioria das surpresas aparece. Nas nossas propostas isso vem discriminado à partida.`,
    categoriasTitle: 'Tipos de instalação que executamos',
    categoriasSubtitle: 'O sistema certo depende do número de divisões, do tipo de teto e do que a fachada permite',
    categorias: [
      {
        title: 'Split — uma divisão',
        icon: 'ri-temp-cold-line',
        image: '/images/ar-condicionado-moderno.webp',
        description: 'A instalação mais comum: uma unidade interior ligada a uma exterior. Rápida de executar, normalmente concluída em meio dia. Indicada para quartos, salas e escritórios individuais.',
        itens: [
          'Executada em meio dia',
          'Ideal para quarto ou sala',
          'Tubagem à vista ou embutida',
          'Ligação elétrica dedicada'
        ]
      },
      {
        title: 'Multi-Split — várias divisões',
        icon: 'ri-layout-grid-line',
        image: '/images/climatizacao-hero.webp',
        description: 'Uma só unidade exterior alimenta duas a cinco unidades interiores. Poupa espaço na fachada, reduz o ruído no exterior e é muitas vezes a única solução aceite em condomínios que limitam o número de máquinas visíveis.',
        itens: [
          'Uma unidade exterior para várias divisões',
          'Menos impacto visual na fachada',
          'Controlo independente por divisão',
          'Um a três dias de execução'
        ]
      },
      {
        title: 'Condutas e Teto Falso',
        icon: 'ri-blaze-line',
        image: '/images/ar-condicionado-condutas.webp',
        description: 'A instalação mais discreta: a unidade fica oculta no teto falso e apenas as grelhas ficam à vista. Exige coordenação com a obra ou com o instalador de pladur, por isso é a solução natural quando há pré-instalação.',
        itens: [
          'Equipamento totalmente oculto',
          'Grelhas e difusores à medida',
          'Coordenação com pladur e teto falso',
          'Climatiza várias zonas de uma vez'
        ]
      },
      {
        title: 'VRF e Espaços Comerciais',
        icon: 'ri-building-2-line',
        image: '/images/manutencao-condominios-edificios.webp',
        description: 'Para escritórios, lojas, hotéis e edifícios com muitas zonas a climatizar. Os sistemas VRF permitem controlo individual por espaço e uma eficiência muito superior à de vários equipamentos independentes.',
        itens: [
          'Controlo individual por zona',
          'Grande eficiência energética',
          'Monitorização e gestão centralizada',
          'Projeto e dimensionamento incluídos'
        ]
      }
    ],
    steps: [
      { icon: 'ri-home-4-line', title: 'Visita técnica', description: 'Avaliamos o espaço, o isolamento e os percursos possíveis de tubagem.' },
      { icon: 'ri-calculator-line', title: 'Dimensionamento e proposta', description: 'Calculamos a potência necessária e apresentamos proposta com o modelo e tudo o que está incluído.' },
      { icon: 'ri-tools-line', title: 'Instalação', description: 'Execução completa: unidades, tubagem, drenagem e ligação elétrica.' },
      { icon: 'ri-shield-check-line', title: 'Colocação em serviço', description: 'Vácuo, teste de estanquidade, medições e explicação do funcionamento.' }
    ],
    faqs: [
      { question: 'Que potência preciso para a minha divisão?', answer: 'Depende da área, da orientação solar, do isolamento e do número de pessoas. Na visita técnica gratuita avaliamos tudo isto e indicamos a potência certa antes de comprar.' },
      { question: 'Quanto tempo demora a instalação?', answer: 'Um split simples fica pronto em meio dia. Multi-splits e sistemas de condutas podem exigir um a três dias, consoante os percursos de tubagem.' },
      { question: 'Preciso de autorização do condomínio?', answer: 'Para colocar a unidade exterior em fachada ou zona comum, normalmente sim. Aconselhamo-lo sobre a melhor localização e sobre o que costuma ser exigido.' },
      { question: 'Vendem também o equipamento?', answer: 'Sim. Pode comprar connosco com instalação incluída, ou instalamos equipamento que já tenha adquirido.' }
    ]
  },
  {
    id: 'venda-ar-condicionado',
    slug: 'venda-ar-condicionado',
    navTitle: 'Venda',
    tituloLocal: 'Venda de Ar Condicionado',
    formValue: 'Venda de Equipamento',
    title: 'Venda de Ar Condicionado e Equipamentos de Climatização',
    subtitle: 'As principais marcas do mercado, com instalação e garantia incluídas',
    description: 'Venda de ar condicionado em Lisboa: split, multi-split, condutas, portáteis e bombas de calor. Aconselhamento técnico gratuito e instalação incluída.',
    image: '/images/bomba-calor.webp',
    imagePosition: 'center 40%',
    beneficios: [
      'Aconselhamento técnico antes da compra, sem compromisso',
      'Equipamento e instalação na mesma fatura e na mesma garantia',
      'Visita técnica gratuita para definir a potência certa',
      'Comparação honesta entre gamas e classes energéticas',
      'Um único interlocutor se algo correr mal'
    ],
    features: [
      'Ar condicionado split e multi-split',
      'Ar condicionado de condutas',
      'Consolas de teto e de chão',
      'Ar condicionado portátil',
      'Bombas de calor e AQS',
      'Desumidificadores'
    ],
    fullContent: `A ALPHA CLIMATIZAÇÃO vende equipamentos de climatização em Lisboa com instalação incluída. Trabalhamos com as principais marcas do mercado e com todas as tipologias: split mural, multi-split, ar condicionado de condutas, consolas de teto e de chão, equipamentos portáteis, bombas de calor e desumidificadores.

Comprar o equipamento a quem o vai instalar tem uma vantagem prática que só se percebe quando há problemas: existe um único responsável. Não há discussão sobre se a avaria é do aparelho ou da instalação, nem entre duas empresas a apontar uma para a outra. Equipamento e instalação ficam cobertos pela mesma garantia e resolvidos pelo mesmo interlocutor.

O aconselhamento é gratuito e é técnico, não comercial. Antes de recomendar um modelo, percebemos a área a climatizar, quantas horas por dia o equipamento vai trabalhar, se é para arrefecer, aquecer ou ambos, e qual o orçamento disponível. Explicamos com clareza a diferença entre gamas — nível de ruído, classe energética, tecnologia inverter, filtragem, garantia do fabricante — para que a decisão seja sua e informada. Se um modelo mais barato servir perfeitamente para o seu caso, é isso que lhe dizemos.

Para saber a potência necessária, fazemos uma visita técnica gratuita: avaliamos a área, a exposição solar, o isolamento e o número de ocupantes, e só depois recomendamos o equipamento.

Peça-nos uma proposta com o equipamento e a instalação discriminados. Sem compromisso, e com o preço final fechado antes de qualquer trabalho começar.`,
    categoriasTitle: 'A nossa gama de equipamentos',
    categoriasSubtitle: 'Todas as tipologias de climatização, das principais marcas do mercado, com instalação incluída',
    categorias: [
      {
        title: 'Ar Condicionado Split e Multi-Split',
        icon: 'ri-temp-cold-line',
        image: '/images/ar-condicionado-moderno.webp',
        description: 'A solução mais comum em habitação. O split liga uma unidade interior a uma exterior; o multi-split permite climatizar várias divisões com uma só unidade exterior, poupando espaço na fachada e reduzindo o impacto visual.',
        itens: [
          'Split mural para uma divisão',
          'Multi-split de 2 a 5 unidades interiores',
          'Tecnologia inverter de série',
          'Modelos silenciosos para quartos'
        ]
      },
      {
        title: 'Ar Condicionado de Condutas',
        icon: 'ri-blaze-line',
        image: '/images/ar-condicionado-condutas.webp',
        description: 'A opção mais discreta: a máquina fica escondida no teto falso e só se veem as grelhas de insuflação. Ideal para moradias, remodelações e espaços comerciais onde a estética conta e se pretende climatizar várias zonas com um só equipamento.',
        itens: [
          'Unidade oculta em teto falso',
          'Distribuição por várias divisões',
          'Grelhas e difusores à medida',
          'Ideal para pré-instalação em obra'
        ]
      },
      {
        title: 'Bombas de Calor e AQS',
        icon: 'ri-sun-line',
        image: '/images/bomba-calor.webp',
        description: 'Aquecimento e água quente sanitária com alta eficiência energética. A bomba de calor aproveita a energia do ar exterior, o que a torna a alternativa mais económica ao esquentador ou à caldeira em custo de utilização.',
        itens: [
          'Bombas de calor para aquecimento',
          'Termoacumuladores com bomba de calor',
          'Aquecimento de piscina',
          'Apoio no acesso a apoios e incentivos'
        ]
      },
      {
        title: 'Consolas de Teto e de Chão',
        icon: 'ri-layout-bottom-line',
        image: '/images/manutencao-restauracao-comercio.webp',
        description: 'Para espaços onde a instalação mural não é possível ou não é desejável. As consolas de chão funcionam bem sob janelas e em divisões com pé-direito baixo; as de teto (cassete) distribuem o ar em quatro direções, sendo a escolha habitual em lojas e escritórios.',
        itens: [
          'Cassete de teto de 4 vias',
          'Consola de chão e de teto',
          'Indicado para comércio e escritórios',
          'Distribuição de ar mais uniforme'
        ]
      },
      {
        title: 'Ar Condicionado Portátil',
        icon: 'ri-device-line',
        image: '/images/climatizacao-hero.webp',
        description: 'Quando não é possível fazer obra — arrendamento, condomínios com restrições de fachada ou necessidade pontual. Não substitui um equipamento fixo em eficiência nem em ruído, e dizemo-lo com clareza antes da compra.',
        itens: [
          'Sem obra e sem unidade exterior',
          'Solução para arrendamento',
          'Uso pontual ou sazonal',
          'Aconselhamento honesto sobre limitações'
        ]
      },
      {
        title: 'Desumidificadores e Qualidade do Ar',
        icon: 'ri-water-percent-line',
        image: '/images/manutencao-ac.webp',
        description: 'Humidade, condensação nos vidros e bolor nas paredes raramente se resolvem só com aquecimento. Avaliamos o caso e indicamos o equipamento adequado, que muitas vezes é mais barato do que a solução que o cliente tinha em mente.',
        itens: [
          'Desumidificadores domésticos',
          'Equipamentos para caves e garagens',
          'Purificação e filtragem de ar',
          'Avaliação prévia do problema'
        ]
      }
    ],
    faqs: [
      { question: 'Com que marcas trabalham?', answer: 'Trabalhamos com as principais marcas disponíveis no mercado português. Indicamos as opções para o seu caso concreto e o prazo de entrega de cada uma.' },
      { question: 'Posso comprar só o equipamento?', answer: 'Sim, mas recomendamos a compra com instalação. Uma instalação incorreta feita por terceiros pode comprometer a garantia do fabricante.' },
      { question: 'Qual a diferença entre inverter e não inverter?', answer: 'Um equipamento inverter regula a potência em contínuo em vez de ligar e desligar. Consome menos, faz menos ruído e mantém a temperatura mais estável. Para uso regular compensa quase sempre.' },
      { question: 'Dão garantia?', answer: 'Sim. O equipamento tem a garantia do fabricante e a instalação tem a nossa garantia própria.' }
    ]
  },
  {
    id: 'pre-instalacao-obra',
    slug: 'pre-instalacao-obra',
    navTitle: 'Pré-Instalação para Obra',
    tituloLocal: 'Pré-Instalação de Ar Condicionado para Obra',
    formValue: 'Pré-Instalação para Obra',
    title: 'Pré-Instalação de Ar Condicionado para Obra',
    subtitle: 'Tubagem, drenagem e alimentação elétrica deixadas prontas antes dos acabamentos',
    description: 'Pré-instalação de ar condicionado para obra em Lisboa. Executamos tubagem, drenagem e alimentação elétrica na fase de tosco, em coordenação com o empreiteiro, para que o equipamento seja montado depois sem partir paredes.',
    image: '/images/pre-instalacao-ac.webp',
    imagePosition: 'center 40%',
    beneficios: [
      'Sem paredes partidas depois dos acabamentos',
      'Tubagem embutida e invisível, sem calhas técnicas à vista',
      'Custo muito inferior ao de instalar depois da obra feita',
      'Liberdade para escolher o equipamento mais tarde',
      'Coordenação direta com empreiteiro, arquiteto e eletricista'
    ],
    features: [
      'Projeto de percursos de tubagem',
      'Linhas de refrigeração embutidas e isoladas',
      'Drenagem de condensados com inclinação correta',
      'Alimentação elétrica dedicada e caixas de derivação',
      'Preparação de negativos e passagens em lajes',
      'Ensaio de pressão e selagem das linhas até à montagem'
    ],
    fullContent: `A pré-instalação é o trabalho de climatização que se faz enquanto a obra ainda está em tosco: passar a tubagem de refrigeração, a drenagem de condensados e a alimentação elétrica dentro das paredes e tetos, antes de rebocos, pladur, pinturas e pavimentos. As unidades só são montadas no fim, quando a obra estiver concluída.

É a diferença entre uma instalação invisível e uma instalação improvisada. Quem salta esta fase acaba, quase sempre, com tubagem à vista em calhas técnicas pelas fachadas e pelo interior, ou com um orçamento de reabertura de paredes, reboco e pintura que custa várias vezes mais do que teria custado fazer no momento certo. Numa moradia ou num apartamento em remodelação, esta é a decisão de climatização com maior impacto financeiro — e é tomada muito cedo, quando ninguém está ainda a pensar em ar condicionado.

Trabalhamos em coordenação direta com o empreiteiro, o arquiteto e o eletricista da obra. Definimos em conjunto a localização das unidades interiores e da unidade exterior, os percursos de tubagem, os pontos de drenagem e os quadros de alimentação. Executamos as linhas de refrigeração devidamente isoladas, deixamos a drenagem com a inclinação correta e preparamos os negativos e passagens necessários nas lajes e paredes.

Concluída a pré-instalação, as linhas ficam sob pressão e seladas, e o ensaio fica documentado. Isso garante que, meses depois, quando a obra terminar e as unidades forem montadas, o circuito continua estanque e nada foi danificado entretanto. Se algum troço tiver sido perfurado por outra especialidade no decorrer da obra, sabemos antes de carregar o sistema com gás.

Uma vantagem adicional: a pré-instalação não o obriga a escolher já o equipamento. Deixamos a infraestrutura preparada e a decisão sobre marca, modelo e potência pode ser tomada mais perto do fim da obra. Trabalhamos com construtores, empreiteiros, gabinetes de arquitetura e particulares em remodelação. Peça-nos uma visita à obra e uma proposta.`,
    categoriasTitle: 'Onde fazemos pré-instalação',
    categoriasSubtitle: 'Da moradia nova à remodelação de um T2 — o princípio é o mesmo: passar a infraestrutura antes dos acabamentos',
    categorias: [
      {
        title: 'Construção Nova e Moradias',
        icon: 'ri-home-gear-line',
        image: '/images/pre-instalacao-ac.webp',
        description: 'A situação ideal. Com a obra ainda em tosco há liberdade total para definir onde ficam as unidades interiores, por onde passa a tubagem e onde assenta a unidade exterior, sem condicionamentos nem soluções de recurso.',
        itens: [
          'Percursos definidos em projeto',
          'Tubagem totalmente embutida',
          'Unidade exterior em local discreto',
          'Coordenação com todas as especialidades'
        ]
      },
      {
        title: 'Remodelação de Apartamentos',
        icon: 'ri-hammer-line',
        image: '/images/pre-instalacao-ac.webp',
        description: 'Quem está a remodelar tem uma janela de oportunidade que não volta. Com as paredes abertas, a pré-instalação custa uma fração do que custaria mais tarde — depois de pintado e com pavimento colocado, o valor multiplica-se.',
        itens: [
          'Aproveita as paredes já abertas',
          'Evita calhas técnicas à vista',
          'Custo muito inferior ao de instalar depois',
          'Compatível com qualquer marca futura'
        ]
      },
      {
        title: 'Empreitadas e Promoção Imobiliária',
        icon: 'ri-building-3-line',
        image: '/images/manutencao-condominios-edificios.webp',
        description: 'Trabalhamos com construtores e promotores em edifícios completos, com planeamento por fases e faturação por empreitada. A pré-instalação valoriza a fração e é hoje um argumento de venda esperado pelo comprador.',
        itens: [
          'Planeamento por fases da obra',
          'Equipas dimensionadas para o volume',
          'Faturação por empreitada',
          'Valoriza a fração na venda'
        ]
      },
      {
        title: 'Lojas, Escritórios e Restauração',
        icon: 'ri-store-3-line',
        image: '/images/ar-condicionado-condutas.webp',
        description: 'Em obras de adaptação de espaços comerciais, a climatização tem de ser pensada ao mesmo tempo que o teto falso, a ventilação e a extração. Entramos na fase de projeto para evitar conflitos entre especialidades.',
        itens: [
          'Compatibilização com teto falso',
          'Articulação com ventilação e extração',
          'Preparação para VRF ou condutas',
          'Prazos alinhados com a abertura'
        ]
      }
    ],
    steps: [
      { icon: 'ri-draft-line', title: 'Visita à obra e projeto', description: 'Definimos com o empreiteiro e o arquiteto as posições das unidades e os percursos.' },
      { icon: 'ri-ruler-line', title: 'Execução em tosco', description: 'Tubagem, drenagem e alimentação elétrica passadas antes de rebocos e acabamentos.' },
      { icon: 'ri-test-tube-line', title: 'Ensaio e selagem', description: 'Linhas pressurizadas, ensaiadas e seladas até à montagem dos equipamentos.' },
      { icon: 'ri-check-double-line', title: 'Montagem final', description: 'Terminada a obra, montamos as unidades e colocamos o sistema em serviço.' }
    ],
    faqs: [
      { question: 'Em que fase da obra devo chamar-vos?', answer: 'Antes dos rebocos e do fecho de tetos falsos, e idealmente já com o projeto de eletricidade definido. Quanto mais cedo, mais opções de percurso existem.' },
      { question: 'Tenho de escolher já o equipamento?', answer: 'Não. Precisamos de saber a tipologia e a potência aproximada para dimensionar a tubagem, mas a marca e o modelo podem ser decididos mais tarde.' },
      { question: 'Quanto tempo fica a pré-instalação à espera?', answer: 'Pode ficar meses. As linhas ficam pressurizadas e seladas, o que permite verificar mais tarde se mantêm a estanquidade.' },
      { question: 'Trabalham com empreiteiros e construtores?', answer: 'Sim. Uma parte significativa deste serviço é feita em coordenação com equipas de obra, em moradias, remodelações e edifícios completos.' }
    ]
  },
  {
    id: 'pecas-reposicao',
    slug: 'pecas-reposicao',
    navTitle: 'Peças de Reposição',
    tituloLocal: 'Peças de Reposição para Ar Condicionado',
    formValue: 'Peças de Reposição',
    title: 'Peças de Reposição para Ar Condicionado',
    subtitle: 'Compressores, placas, ventiladores, comandos e consumíveis',
    description: 'Peças de reposição para ar condicionado e equipamentos de climatização em Lisboa. Identificação da peça pelo modelo, peças originais e compatíveis, com opção de substituição por técnico.',
    image: '/images/pecas-ac.webp',
    imagePosition: 'center 40%',
    beneficios: [
      'Identificamos a peça certa a partir do modelo do equipamento',
      'Peças originais e alternativas compatíveis, com a diferença explicada',
      'Evita substituir o equipamento inteiro por uma avaria pontual',
      'Possibilidade de substituição por técnico certificado',
      'Aconselhamento honesto quando reparar já não compensa'
    ],
    features: [
      'Compressores e condensadores',
      'Placas eletrónicas e sensores',
      'Ventiladores e turbinas',
      'Comandos e recetores',
      'Filtros e consumíveis',
      'Válvulas, pressostatos e material de refrigeração'
    ],
    fullContent: `A ALPHA CLIMATIZAÇÃO fornece peças de reposição para ar condicionado e equipamentos de climatização em Lisboa. Compressores, condensadores, placas eletrónicas, sensores de temperatura, ventiladores e turbinas, motores, comandos e recetores, válvulas, pressostatos, filtros e consumíveis de refrigeração.

O primeiro passo é identificar a peça certa. Envie-nos a marca, o modelo e o número de série do equipamento — estão na chapa de características da unidade interior ou exterior — e, sempre que possível, uma fotografia da peça avariada. Com essa informação confirmamos a referência exata, a disponibilidade e o prazo. Peças pedidas por descrição aproximada acabam quase sempre erradas, e é tempo perdido para todos.

Sempre que existe alternativa, apresentamos as duas hipóteses: a peça original do fabricante e a peça compatível de outro fornecedor. Explicamos a diferença real de preço, de garantia e de durabilidade, e deixamos a decisão consigo. Em alguns componentes a peça compatível é uma escolha perfeitamente sensata; noutros, sobretudo em placas eletrónicas e compressores, a original compensa quase sempre.

Pode adquirir apenas a peça, se tiver quem a instale, ou incluir a substituição pelos nossos técnicos. Recomendamos a segunda opção em qualquer intervenção que envolva o circuito de refrigeração: manusear gases fluorados exige certificação própria, equipamento de recolha e vácuo, e uma substituição mal feita danifica o compressor em pouco tempo.

Também lhe dizemos quando não vale a pena. Se o custo da peça e da mão de obra se aproximar do valor de um equipamento novo — algo frequente em compressores de aparelhos com muitos anos e baixa eficiência — dizemos-lho com clareza e mostramos-lhe a comparação. Preferimos perder uma venda de peça a vender-lhe uma reparação que não faz sentido.`,
    categoriasTitle: 'Peças que fornecemos',
    categoriasSubtitle: 'Envie a marca, o modelo e o número de série e confirmamos a referência exata antes de qualquer encomenda',
    categorias: [
      {
        title: 'Compressores e Condensadores',
        icon: 'ri-cpu-line',
        image: '/images/pecas-ac.webp',
        description: 'O coração do equipamento e, também, a peça mais cara. Antes de avançar, verificamos sempre se a substituição compensa face ao valor de um equipamento novo — em aparelhos com muitos anos, frequentemente não compensa.',
        itens: [
          'Compressores para split e multi-split',
          'Condensadores e permutadores',
          'Substituição por técnico certificado',
          'Análise prévia de custo-benefício'
        ]
      },
      {
        title: 'Placas Eletrónicas e Sensores',
        icon: 'ri-hard-drive-2-line',
        image: '/images/pecas-ac.webp',
        description: 'Avarias intermitentes, códigos de erro no visor e equipamentos que não arrancam têm quase sempre origem aqui. Nestes componentes recomendamos peça original: as alternativas compatíveis dão frequentemente problemas de comunicação.',
        itens: [
          'Placas de unidade interior e exterior',
          'Sensores de temperatura e pressão',
          'Diagnóstico do código de erro',
          'Recomendação de peça original'
        ]
      },
      {
        title: 'Ventiladores, Turbinas e Motores',
        icon: 'ri-loader-4-line',
        image: '/images/pecas-ac.webp',
        description: 'Ruído anormal, vibração ou caudal de ar reduzido apontam normalmente para o ventilador ou para os apoios do motor. É uma reparação com boa relação custo-benefício, que devolve o desempenho original ao equipamento.',
        itens: [
          'Turbinas de unidade interior',
          'Ventiladores de unidade exterior',
          'Motores e chumaceiras',
          'Resolve ruído e vibração'
        ]
      },
      {
        title: 'Comandos, Filtros e Consumíveis',
        icon: 'ri-remote-control-line',
        image: '/images/pecas-ac.webp',
        description: 'As peças de substituição mais simples e as únicas que o cliente pode instalar sozinho sem risco. Fornecemos comandos originais e universais, filtros de substituição e todo o material corrente de refrigeração.',
        itens: [
          'Comandos originais e universais',
          'Recetores de infravermelhos',
          'Filtros e filtros de carvão ativado',
          'Válvulas, pressostatos e material de linha'
        ]
      }
    ],
    faqs: [
      { question: 'Como sei qual é a peça de que preciso?', answer: 'Envie-nos a marca, o modelo e o número de série da chapa de características, mais uma foto da peça. Confirmamos a referência exata antes de qualquer encomenda.' },
      { question: 'Vendem peças para qualquer marca?', answer: 'Conseguimos peças para a generalidade das marcas do mercado. Em modelos muito antigos ou descontinuados, procuramos alternativas compatíveis.' },
      { question: 'Posso instalar a peça eu próprio?', answer: 'Em consumíveis e comandos, sim. Em tudo o que envolva o circuito de refrigeração, não — é necessária certificação para manuseamento de gases fluorados e equipamento próprio.' },
      { question: 'Vale sempre a pena reparar?', answer: 'Não. Quando o custo da reparação se aproxima do preço de um equipamento novo mais eficiente, dizemos-lho e apresentamos-lhe as duas contas.' }
    ]
  }
];

// Mapa por id, no formato que a página /servico/:id consome
export const climatizacaoServices: Record<string, ClimatizacaoService> =
  climatizacaoServicesList.reduce((acc, service) => {
    acc[service.id] = service;
    return acc;
  }, {} as Record<string, ClimatizacaoService>);

export default climatizacaoServices;
